import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Mail, Phone, MapPin, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { COMPANY, SERVICES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  company: "",
  service_interest: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name || form.name.trim().length < 2) e.name = "Enter your full name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email.";
    if (!form.message || form.message.trim().length < 5)
      e.message = "Tell us a bit more (min 5 characters).";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") delete payload[k];
      });
      await axios.post(`${API}/inquiries`, payload);
      toast.success("Thanks — we'll get back within one business day.");
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="py-20 md:py-28 border-b border-hairline">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <div className="overline text-terracotta mb-4">Contact</div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight leading-[0.98] text-ink">
              Let&rsquo;s build the
              <br />
              <span className="italic font-light text-forest">right team for you.</span>
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-muted2 leading-relaxed">
              Share a few details — a workforce advisor will respond within one business day.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Form */}
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-7 bg-white border border-black/5 p-8 md:p-12"
            noValidate
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="name" className="text-xs uppercase tracking-[0.2em] text-muted2">
                  Full name*
                </Label>
                <Input
                  id="name"
                  data-testid="contact-name"
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  placeholder=""
                  className="mt-2 h-12 border-hairline focus-visible:ring-2 focus-visible:ring-forest"
                />
                {errors.name && (
                  <p data-testid="error-name" className="mt-1 text-xs text-red-600">{errors.name}</p>
                )}
              </div>
              <div>
                <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] text-muted2">
                  Work email*
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="contact-email"
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  placeholder=""
                  className="mt-2 h-12 border-hairline focus-visible:ring-2 focus-visible:ring-forest"
                />
                {errors.email && (
                  <p data-testid="error-email" className="mt-1 text-xs text-red-600">{errors.email}</p>
                )}
              </div>
              <div>
                <Label htmlFor="phone" className="text-xs uppercase tracking-[0.2em] text-muted2">
                  Phone
                </Label>
                <Input
                  id="phone"
                  data-testid="contact-phone"
                  value={form.phone}
                  onChange={(e) => setField("phone", e.target.value)}
                  placeholder=""
                  className="mt-2 h-12 border-hairline focus-visible:ring-2 focus-visible:ring-forest"
                />
              </div>
              <div>
                <Label htmlFor="company" className="text-xs uppercase tracking-[0.2em] text-muted2">
                  Company
                </Label>
                <Input
                  id="company"
                  data-testid="contact-company"
                  value={form.company}
                  onChange={(e) => setField("company", e.target.value)}
                  placeholder="Your company"
                  className="mt-2 h-12 border-hairline focus-visible:ring-2 focus-visible:ring-forest"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-muted2">
                  Service interest
                </Label>
                <Select
                  value={form.service_interest}
                  onValueChange={(v) => setField("service_interest", v)}
                >
                  <SelectTrigger
                    data-testid="contact-service"
                    className="mt-2 h-12 border-hairline focus:ring-2 focus:ring-forest"
                  >
                    <SelectValue placeholder="Choose a service (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem key={s.slug} value={s.title} data-testid={`svc-opt-${s.slug}`}>
                        {s.title}
                      </SelectItem>
                    ))}
                    <SelectItem value="Not sure yet" data-testid="svc-opt-other">
                      Not sure yet
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="message" className="text-xs uppercase tracking-[0.2em] text-muted2">
                  How can we help?*
                </Label>
                <Textarea
                  id="message"
                  data-testid="contact-message"
                  value={form.message}
                  onChange={(e) => setField("message", e.target.value)}
                  placeholder="Tell us about the role, team, or mandate..."
                  rows={6}
                  className="mt-2 border-hairline focus-visible:ring-2 focus-visible:ring-forest resize-none"
                />
                {errors.message && (
                  <p data-testid="error-message" className="mt-1 text-xs text-red-600">{errors.message}</p>
                )}
              </div>
            </div>

            <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
              <p className="text-xs text-muted2">
                We reply within 1 business day. Your details are never shared.
              </p>
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={submitting}
                className="btn-primary disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    Sending <Loader2 className="w-4 h-4 animate-spin" />
                  </>
                ) : (
                  <>
                    Send enquiry <ArrowUpRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Direct contact */}
          <aside className="lg:col-span-5">
            <div className="bg-forest text-bone p-8 md:p-12">
              <div className="overline text-terracotta-soft mb-4">Prefer direct?</div>
              <h3 className="font-display text-3xl font-medium tracking-tight">
                Speak with a workforce advisor.
              </h3>

              <div className="mt-10 space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-bone/10 flex items-center justify-center shrink-0">
                    <Mail className="w-4 h-4" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-bone/60">Email</div>
                    <a
                      href={`mailto:${COMPANY.email}`}
                      data-testid="direct-email"
                      className="text-bone hover:text-terracotta-soft"
                    >
                      {COMPANY.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-bone/10 flex items-center justify-center shrink-0">
                    <Phone className="w-4 h-4" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-bone/60">Phone</div>
                    <a
                      href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
                      data-testid="direct-phone"
                      className="text-bone hover:text-terracotta-soft"
                    >
                      {COMPANY.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-bone/10 flex items-center justify-center shrink-0">
                    <MapPin className="w-4 h-4" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-bone/60">Office</div>
                    <div data-testid="direct-address" className="text-bone/85 leading-relaxed">
                      {COMPANY.address}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-12 pt-8 border-t border-bone/15">
                <div className="text-xs uppercase tracking-[0.2em] text-bone/60 mb-2">Office hours</div>
                <div className="text-bone/85">Mon – Fri, 9:30 AM – 6:30 PM IST</div>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
